import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  EmptyState,
  Pagination,
  Select,
  Spinner,
  Tabs,
  TextField,
  Typography,
} from '@glplus/glpweb-dsl';
import { useVendor } from '../lib/CapabilitiesContext';
import { useLocationScope } from '../lib/LocationScopeContext';
import {
  vendorApiClient,
  type PromotionSale,
  type PromotionSalesResponse,
  type SaleEndingFilter,
} from '../lib/vendorApiClient';
import { formatCents, formatDay } from '../lib/money';
import { BORDER, GREEN_INK, HEADING, MUTED, ORANGE_INK, PageTitle, describeProductError } from './products/parts';

const ALL_LOCATIONS = '__all';
const PAGE_SIZE = 25;
const ENDING: SaleEndingFilter[] = ['any', 'seven_days', 'thirty_days', 'no_end'];
const GRID = { xs: '1fr auto', md: '2.2fr 1.5fr 1fr 1fr 1.15fr 1.1fr' };

/** Products-on-sale half of the approved Promotions design (Figma 96:147). */
export default function Promotions() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { activeVendorId, has } = useVendor();
  const { scope } = useLocationScope();
  const canEdit = has('catalog:edit');
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const ending = (ENDING.includes(params.get('ending') as SaleEndingFilter) ? params.get('ending') : 'any') as SaleEndingFilter;
  const explicitStoreId = params.get('storeId') ?? '';
  const storeId = explicitStoreId || (scope !== 'all' ? scope : '');

  const [data, setData] = useState<PromotionSalesResponse | null>(null);
  const [search, setSearch] = useState(q);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [endingSale, setEndingSale] = useState<PromotionSale | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const requestSeq = useRef(0);

  const patchParams = useCallback(
    (patch: Record<string, string | null>, keepPage = false) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(patch)) {
            if (!value) next.delete(key);
            else next.set(key, value);
          }
          if (!keepPage) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (search.trim() !== q) patchParams({ q: search.trim() || null });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [patchParams, q, search]);

  const load = useCallback(() => {
    if (!activeVendorId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setLoadError(null);
    vendorApiClient
      .listPromotionSales(activeVendorId, { q: q || undefined, storeId: storeId || undefined, ending, page, pageSize: PAGE_SIZE })
      .then((result) => {
        if (requestSeq.current === seq) setData(result);
      })
      .catch((error: Error) => {
        if (requestSeq.current === seq) setLoadError(describeProductError(error, t));
      })
      .finally(() => {
        if (requestSeq.current === seq) setLoading(false);
      });
  }, [activeVendorId, ending, page, q, storeId, t]);

  useEffect(load, [load]);

  const endSale = async () => {
    if (!activeVendorId || !endingSale) return;
    setBusy(true);
    setActionError(null);
    try {
      await vendorApiClient.bulkUpdateProducts(activeVendorId, {
        storeId: endingSale.location.storeId,
        vendorProductIds: [endingSale.vendorProductId],
        action: { type: 'end_sale' },
      });
      setEndingSale(null);
      load();
    } catch (error) {
      setActionError(describeProductError(error, t));
    } finally {
      setBusy(false);
    }
  };

  if (loadError && !data) return <Alert severity="error">{loadError}</Alert>;
  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  const pages = Math.max(1, Math.ceil(data.total / data.pageSize));

  return (
    <Box data-testid="promotions-sales">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <PageTitle title={t('promotions.title')} subtitle={t('promotions.subtitle')} />
        {canEdit && (
          <Button
            ariaLabel={t('promotions.putOnSale')}
            buttonText={t('promotions.putOnSale')}
            variant="contained"
            color="primary"
            startIcon="add"
            onClick={() => navigate('/products')}
          />
        )}
      </Box>

      <Tabs
        ariaLabel={t('promotions.tabsAria')}
        value="sales"
        onChange={(_event, value) => value === 'circulars' && navigate('/promotions/circulars')}
        color={ORANGE_INK}
        sx={{ mt: 2 }}
        tabs={[
          { value: 'circulars', label: t('circulars.tab') },
          { value: 'sales', label: t('promotions.sales.tab'), count: data.total },
        ]}
      />

      <Box sx={{ mt: 2.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ flex: '1 1 320px' }}>
          <TextField
            ariaLabel={t('promotions.searchAria')}
            placeholder={t('promotions.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            size="small"
            fullWidth
          />
        </Box>
        <Box sx={{ flex: '0 1 260px', minWidth: 220 }}>
          <Select
            ariaLabel={t('promotions.location')}
            label={t('promotions.location')}
            value={storeId || ALL_LOCATIONS}
            onChange={(_event, value) => patchParams({ storeId: value === ALL_LOCATIONS ? null : value })}
            options={[
              { value: ALL_LOCATIONS, label: t('promotions.allLocations') },
              ...data.locations.map((location) => ({ value: location.storeId, label: location.name })),
            ]}
            size="small"
            fullWidth
          />
        </Box>
        <Box sx={{ flex: '0 1 220px', minWidth: 190 }}>
          <Select
            ariaLabel={t('promotions.ending.label')}
            label={t('promotions.ending.label')}
            value={ending}
            onChange={(_event, value) => patchParams({ ending: value === 'any' ? null : value })}
            options={ENDING.map((value) => ({ value, label: t(`promotions.ending.${value}`) }))}
            size="small"
            fullWidth
          />
        </Box>
      </Box>

      {loadError && <Alert severity="error" sx={{ mt: 2 }}>{loadError}</Alert>}
      {loading && <Typography sx={{ mt: 1, color: MUTED, fontSize: 13 }}>{t('common.loading')}</Typography>}

      {data.items.length === 0 ? (
        <Box sx={{ mt: 2 }}>
          <EmptyState
            icon="loyalty"
            title={t('promotions.empty.title')}
            body={q || storeId || ending !== 'any' ? t('promotions.empty.filtered') : t('promotions.empty.body')}
            primaryAction={canEdit ? { label: t('promotions.putOnSale'), onClick: () => navigate('/products') } : undefined}
            backgroundColor="#fff"
            border={`1px solid ${BORDER}`}
            borderRadius={10}
            iconColor={ORANGE_INK}
          />
        </Box>
      ) : (
        <Box role="table" aria-label={t('promotions.tableAria')} sx={{ mt: 2, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden' }}>
          <Box role="row" sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: GRID.md, gap: 2, px: 2.5, py: 1.5, bgcolor: '#F9FAFB', borderBottom: `1px solid ${BORDER}` }}>
            {['product', 'location', 'salePrice', 'wasPrice', 'ends', 'actions'].map((key) => (
              <Typography key={key} role="columnheader" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: MUTED }}>
                {t(`promotions.columns.${key}`).toUpperCase()}
              </Typography>
            ))}
          </Box>
          {data.items.map((sale) => (
            <SaleRow
              key={`${sale.vendorProductId}-${sale.location.storeId}`}
              sale={sale}
              language={i18n.language}
              canEdit={canEdit}
              onEdit={() => navigate(`/products/${sale.vendorProductId}`)}
              onEnd={() => {
                setActionError(null);
                setEndingSale(sale);
              }}
            />
          ))}
        </Box>
      )}

      {pages > 1 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Pagination
            totalPages={pages}
            page={Math.min(page, pages)}
            onPageChange={(value) => patchParams({ page: value === 1 ? null : String(value) }, true)}
            ariaLabel={t('promotions.pagination')}
            activeBgColor="#25862D"
            activeColor="#FFFFFF"
          />
        </Box>
      )}

      {endingSale && (
        <Dialog open onClose={busy ? undefined : () => setEndingSale(null)} maxWidth="sm" fullWidth ariaLabelledBy="end-sale-title">
          <DialogTitle id="end-sale-title">{t('promotions.endDialog.title')}</DialogTitle>
          <DialogContent>
            <Typography>{t('promotions.endDialog.body', { product: endingSale.name, location: endingSale.location.name })}</Typography>
            {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button ariaLabel={t('common.cancel')} buttonText={t('common.cancel')} variant="text" onClick={() => setEndingSale(null)} disabled={busy} />
            <Button ariaLabel={t('promotions.endDialog.confirm')} buttonText={t('promotions.endDialog.confirm')} variant="contained" color="primary" onClick={() => void endSale()} disabled={busy} />
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

function SaleRow({ sale, language, canEdit, onEdit, onEnd }: { sale: PromotionSale; language: string; canEdit: boolean; onEdit: () => void; onEnd: () => void }) {
  const { t } = useTranslation();
  return (
    <Box role="row" sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, alignItems: 'center', px: 2.5, py: 2, borderBottom: `1px solid ${BORDER}`, '&:last-of-type': { borderBottom: 'none' } }}>
      <Box role="cell" sx={{ minWidth: 0 }}>
        <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: 15 }}>{sale.name}</Typography>
        <Typography sx={{ color: MUTED, fontSize: 13 }}>{[sale.brand, sale.vendorSku].filter(Boolean).join(' · ')}</Typography>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Chip label={sale.state === 'scheduled' ? t('promotions.state.scheduled') : t('promotions.state.active')} size="small" backgroundColor={sale.state === 'scheduled' ? '#EEF1F8' : '#EAF5EA'} textColor={sale.state === 'scheduled' ? '#343F5B' : GREEN_INK} fontSize={12} fontWeight={700} />
          {!sale.visibleToShoppers && <Chip label={t('promotions.notVisible')} size="small" backgroundColor="#FEF1E7" textColor={ORANGE_INK} fontSize={12} fontWeight={700} />}
        </Box>
      </Box>
      <Box role="cell" sx={{ display: { xs: 'none', md: 'block' } }}>
        <Typography sx={{ color: HEADING, fontSize: 14 }}>{sale.location.name}</Typography>
        {!sale.visibleToShoppers && <Typography sx={{ color: ORANGE_INK, fontSize: 12 }}>{t('promotions.notVisible')}</Typography>}
      </Box>
      <Typography role="cell" sx={{ display: { xs: 'none', md: 'block' }, color: GREEN_INK, fontWeight: 800 }}>{formatCents(sale.salePriceCents)}</Typography>
      <Typography role="cell" sx={{ display: { xs: 'none', md: 'block' }, color: MUTED, textDecoration: 'line-through' }}>{formatCents(sale.priceCents)}</Typography>
      <Box role="cell" sx={{ display: { xs: 'none', md: 'block' } }}>
        <Typography sx={{ color: HEADING, fontSize: 14 }}>{sale.saleEndsOn ? formatDay(sale.saleEndsOn, language) : t('promotions.noEnd')}</Typography>
        <Typography sx={{ color: MUTED, fontSize: 12 }}>{sale.state === 'scheduled' ? t('promotions.starts', { date: formatDay(sale.saleStartsOn, language) }) : t('promotions.state.active')}</Typography>
      </Box>
      <Box role="cell" sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button ariaLabel={t('promotions.editAria', { product: sale.name })} buttonText={t('common.edit')} variant="text" onClick={onEdit} />
        {canEdit && <Button ariaLabel={t('promotions.endAria', { product: sale.name })} buttonText={t('promotions.endSale')} variant="text" onClick={onEnd} />}
      </Box>
    </Box>
  );
}
